import { useEffect, useState } from "react";

import { usePostHog } from "posthog-js/react";

import { useAPI, useShipperShipmentPageContext } from "hooks";

import { CancelShipmentForm } from "./CancelShipmentForm";
import { BOOKED_OFF_PLATFORM_TEXT, OTHER_REASON_OPTION } from "./constants";
import { ClientAppEvent } from "../../../../constants";

import type { PendingOffPlatformBooking } from "hooks";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  shipment: CondensedShipment;
  onRecordOffPlatformBooking?: VoidFunction;
  pendingOffPlatformBooking: PendingOffPlatformBooking | null;
}

export function CancelShipmentFormContainer(props: Props) {
  const {
    open,
    onClose,
    shipment,
    onRecordOffPlatformBooking,
    pendingOffPlatformBooking,
  } = props;
  const [error, setError] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  // Held here with the reason it writes so both reset together on close; the
  // dialog content keeps rendering through MUI's close transition.
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string>("");
  const { onRefresh } = useShipperShipmentPageContext();
  const { api } = useAPI();
  const posthog = usePostHog();

  // The container outlives the dialog, so it reacts to each opening rather
  // than to mounting.
  useEffect(() => {
    if (open) {
      posthog?.capture(ClientAppEvent.CANCEL_SHIPMENT_MODAL_OPENED, {
        shipment_id: shipment.info.id,
        shipment_type: shipment.info.type,
      });
    }
    // Only an opening should capture, not a change of shipment while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Sends the selected option rather than the typed "Other" text: shippers can
  // write anything there, and the text itself is stored with the reason.
  const captureShipmentCancelled = (): void => {
    posthog?.capture(ClientAppEvent.SHIPMENT_CANCELLED, {
      shipment_id: shipment.info.id,
      shipment_type: shipment.info.type,
      cancellation_reason: selectedOption,
      has_custom_reason:
        selectedOption === OTHER_REASON_OPTION && cancellationReason !== "",
      booked_off_platform_prompt_shown:
        selectedOption === BOOKED_OFF_PLATFORM_TEXT &&
        onRecordOffPlatformBooking !== undefined,
    });
  };

  const onModalClose = () => {
    setError(null);
    setSelectedOption(null);
    setCancellationReason("");
    onClose();
  };

  // Posting a reason notifies every carrier who quoted on the shipment, since the
  // feedback this form writes is always public. Booked off platform takes the same
  // path an empty reason takes: the shipment is cancelled, no feedback is stored
  // and no carrier is notified. The reason still reaches PostHog on
  // SHIPMENT_CANCELLED, so the choice is still measurable.
  // TODO: drop this and let the reason post like the others once product decides
  // whether quoting carriers should be told the shipper booked the load elsewhere.
  const isReasonHeldBack = selectedOption === BOOKED_OFF_PLATFORM_TEXT;

  const postFeedback = (): void => {
    const body = { reason: cancellationReason, is_public: true };
    api<unknown>(
      "post",
      `/shipments/${shipment.info.id}/cancellation-reason`,
      body,
    ).then(() => {
      if (onRefresh) {
        onRefresh();
      }
    });
  };

  const cancelShipment = (): void => {
    setIsCancelling(true);
    api<unknown>("post", `/shipments/${shipment.info.id}/cancel`)
      .then(() => {
        captureShipmentCancelled();
        if (pendingOffPlatformBooking !== null) {
          posthog?.capture(
            ClientAppEvent.SHIPMENT_CANCELLED_WITH_PENDING_OFF_PLATFORM_BOOKING,
            {
              shipment_id: shipment.info.id,
              shipment_type: shipment.info.type,
            },
          );
        }
        onModalClose();
        if (cancellationReason !== "" && !isReasonHeldBack) {
          postFeedback();
        } else if (onRefresh) {
          onRefresh();
        }
      })
      .catch((e) => {
        if (e?.response?.data?.message) {
          setError(e?.response?.data?.message);
        } else if (e?.response.data?.detail) {
          setError(e?.response.data?.detail);
        } else {
          setError("Failed to cancel shipment");
        }
      })
      .finally(() => {
        setIsCancelling(false);
      });
  };

  const onSelectedOptionChange = (option: string) => {
    setSelectedOption(option);
    setCancellationReason(option === OTHER_REASON_OPTION ? "" : option);
  };

  // Switches to the report rather than cancelling: the report needs the
  // shipment to stay active so support can book it.
  const onRecordBooking =
    onRecordOffPlatformBooking === undefined
      ? undefined
      : () => {
          onModalClose();
          onRecordOffPlatformBooking();
        };

  return (
    <CancelShipmentForm
      open={open}
      onClose={onModalClose}
      onCancelShipment={cancelShipment}
      error={error}
      loading={isCancelling}
      selectedOption={selectedOption}
      onSelectedOptionChange={onSelectedOptionChange}
      cancellationReason={cancellationReason}
      onCancellationUpdate={(newValue: string) =>
        setCancellationReason(newValue)
      }
      shipment={shipment}
      onRecordOffPlatformBooking={onRecordBooking}
      pendingOffPlatformBooking={pendingOffPlatformBooking}
    />
  );
}
